// RepoAnalyzer.js - Updated version
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FileText,
  Cpu,
  MessageSquare,
  BookOpen,
  StickyNote,
  Wand2,
  GitCompare,
  Map,
  Settings,
} from "lucide-react";
import api from "./api";
import { useAuth } from "./AuthContext";
import { FileSummary } from "./FileSummary";
import { FileTree } from "./FileTree";
// CodeGraph and ProcessorResults kept for future use - currently hidden from UI
// import CodeGraph from "./CodeGraph";
// import ProcessorResults from "./ProcessorResults";
import FileContent from "./FileContent";
import RepoChatWindow from "./RepoChatWindow";
import SystemOverview from "./SystemOverview";
import RepoTracesPanel from "./RepoTracesPanel";
import NotesPanel from "./NotesPanel";
import FeaturePlansPanel from "./FeaturePlansPanel";
import RoadmapPanel from "./RoadmapPanel";
import SourceComparePanel from "./SourceComparePanel";
import ErrorBoundary from "./ErrorBoundary";
import { lookupFileSummary } from "./utils";
import "./App.css";
import "./RepoAnalyzer.css";
import "./RepoChatWindow.css";
import "./SystemOverview.css";
import "./RepoTracesPanel.css";
import "./NotesPanel.css";
import "./FeaturePlansPanel.css";
import "./RoadmapPanel.css";
import "./SourceComparePanel.css";
import "./ErrorBoundary.css";
import ProgressBar from "./ProgressBar";
import CreditPurchaseModal from "./CreditPurchaseModal";
import { parseGithubRepoInput } from "./parseGithubRepoInput";
import { listBranchAnalyses, preflightGithubPullRequest, submitGithubPullRequestAnalysis } from "./api";
import {
  branchLabelFromRepo,
  branchDisplayNameFromSource,
  isWeakBranchDisplayName,
  formatBranchSelectorLabel,
  mergeBranchAnalyses,
  branchAnalysesEqual,
} from "./sourceCompareDisplay";
import { canMutateRepos } from "./orgPermissions";
import { GitBranch } from "lucide-react";
import RepositoryAnalysisConfirmation from "./RepositoryAnalysisConfirmation";
import {
  buildAnalysisProgress,
  deriveAnalysisStage,
  formatAnalysisProgressCopy,
  formatAnalysisStageLabel,
} from "./analysisProgress";
import { taskPath } from "./taskRoutes";

/** True when repo has what File Explorer needs (avoid wiping hydrated GET /repos/:id with a shallow profile row). */
function withCanonicalBranchAnalysis(analyses, repo) {
  const list = Array.isArray(analyses) ? [...analyses] : [];
  if (!repo?._id) return list;
  const branch = branchLabelFromRepo(repo);
  const existing = list.find((analysis) => String(analysis.repoId) === String(repo._id));
  if (existing) {
    if (isWeakBranchDisplayName(existing.branch, repo) && !isWeakBranchDisplayName(branch, repo)) {
      existing.branch = branch;
    }
    return list;
  }
  list.unshift({
    repoId: repo._id,
    branch,
    status: repo.status,
    commitSha: repo.analysisSource?.commitSha || null,
  });
  return list;
}

function repoHasExplorerPayload(repo) {
  if (!repo) return false;
  const s = repo.structure;
  const hasStructure =
    s &&
    ((Array.isArray(s.files) && s.files.length > 0) ||
      (Array.isArray(s.folders) && s.folders.length > 0));
  const fs = repo.fileSummaries;
  const hasSummaries =
    fs &&
    typeof fs === "object" &&
    !Array.isArray(fs) &&
    Object.keys(fs).length > 0;
  return hasStructure && hasSummaries;
}

const RepoAnalyzer = ({
  initialRepo = null,
  initialTaskId = null,
  initialTaskSection = null,
  initialTaskDiscuss = null,
  initialTaskFocus = null,
  initialTaskItem = null,
  initialTasksFilter = "all",
  onRepoSubmitted,
  onOpenNewRepository,
  onTaskRoute,
}) => {
  const { user } = useAuth();
  const [input, setInput] = useState(() => {
    try {
      const prefill = sessionStorage.getItem("renderrepo.newRepoPrefill");
      if (prefill) sessionStorage.removeItem("renderrepo.newRepoPrefill");
      return prefill || "";
    } catch {
      return "";
    }
  });
  const [activeRepo, setActiveRepo] = useState(initialRepo);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [processingStatus, setProcessingStatus] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [processingFiles, setProcessingFiles] = useState([]);

  // New state for processors and credit estimation
  const [availableProcessors, setAvailableProcessors] = useState([]);
  const [selectedProcessors, setSelectedProcessors] = useState([]);
  const [processorScope, setProcessorScope] = useState("repository"); // Default to repository
  const [, setLoadingProcessors] = useState(false);
  const [creditEstimate, setCreditEstimate] = useState(null);
  const [analysisQuoteToken, setAnalysisQuoteToken] = useState(null);
  const [prPreflight, setPrPreflight] = useState(null);
  const [showEstimate, setShowEstimate] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [activeTab, setActiveTab] = useState("files");
  const [tasksFilter, setTasksFilter] = useState(initialTasksFilter === "review" ? "review" : "all");
  const [workBriefPlanId, setWorkBriefPlanId] = useState(null);
  const [chatPrefilledQuery, setChatPrefilledQuery] = useState(null);
  const [devMode, setDevMode] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  /** Dashboard repos omit `structure`; SSE / GET fills it. Avoid showing "no structure" for ~10s while hydrating. */
  const [structureHydrationTimedOut, setStructureHydrationTimedOut] = useState(false);

  const [branchAnalyses, setBranchAnalyses] = useState(() =>
    withCanonicalBranchAnalysis([], initialRepo)
  );
  const [selectedBranchRepoId, setSelectedBranchRepoId] = useState(null);
  const [branchRepo, setBranchRepo] = useState(null);
  const [viewingSourceTitle, setViewingSourceTitle] = useState(null);

  // SSE: single connection per active repo, batched UI updates
  const eventSourceRef = useRef(null);
  const activeRepoIdRef = useRef(null);
  const pendingUpdateRef = useRef(null);
  const timerRef = useRef(null);
  const fetchFinalDataRef = useRef(null);
  /** Caps repeated GET /repos/:id if the payload stays incomplete (limits repeated work). */
  const completedRepoHydrateAttemptsRef = useRef({});
  const BATCH_MS = 250;

  // Fetch metadata once when a completed repo is first loaded
  const metadataFetchedForRef = useRef(null);
  useEffect(() => {
    const fetchMetadataIfNeeded = async () => {
      if (!activeRepo?._id || activeRepo.status !== 'completed') return;
      if (metadataFetchedForRef.current === activeRepo._id) return;

      const needsMetadata = !activeRepo.metadata ||
                           !activeRepo.metadata.entrypoints?.length ||
                           !activeRepo.metadata.framework;

      if (!needsMetadata) return;

      metadataFetchedForRef.current = activeRepo._id;
      try {
        setMetadataLoading(true);
        const response = await api.getRepoMetadata(activeRepo._id);
        setActiveRepo(prev => ({
          ...prev,
          metadata: response.data
        }));
      } catch (err) {
        console.warn('Failed to fetch metadata:', err);
      } finally {
        setMetadataLoading(false);
      }
    };

    fetchMetadataIfNeeded();
  }, [activeRepo?._id, activeRepo?.status, activeRepo?.metadata]);

  useEffect(() => {
    if (!activeRepo?._id) {
      setStructureHydrationTimedOut(false);
      return;
    }
    setStructureHydrationTimedOut(false);
    const t = window.setTimeout(() => setStructureHydrationTimedOut(true), 12000);
    return () => window.clearTimeout(t);
  }, [activeRepo?._id]);

  // Fetch notes when repo is loaded
  useEffect(() => {
    const fetchNotes = async () => {
      if (!activeRepo?._id) {
        setNotes([]);
        return;
      }

      try {
        const response = await api.getNotes(activeRepo._id);
        setNotes(response.data.notes || []);
      } catch (err) {
        console.warn('Failed to fetch notes:', err);
        // Non-fatal: continue without notes
        setNotes([]);
      }
    };

    fetchNotes();
  }, [activeRepo?._id]);

  useEffect(() => {
    if (!activeRepo?._id) {
      setBranchAnalyses([]);
      setSelectedBranchRepoId(null);
      setBranchRepo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await listBranchAnalyses(activeRepo._id);
        if (!cancelled) {
          setBranchAnalyses(withCanonicalBranchAnalysis(data.analyses || [], activeRepo));
          setSelectedBranchRepoId(null);
          setBranchRepo(null);
          setViewingSourceTitle(null);
        }
      } catch (err) {
        if (!cancelled) {
          setBranchAnalyses(withCanonicalBranchAnalysis([], activeRepo));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeRepo?._id]); // eslint-disable-line react-hooks/exhaustive-deps -- keyed by id; object identity changes on poll

  const handleBranchSwitch = async (branchRepoId) => {
    if (!branchRepoId || branchRepoId === activeRepo?._id) {
      setSelectedBranchRepoId(null);
      setBranchRepo(null);
      setViewingSourceTitle(null);
      return;
    }
    setSelectedBranchRepoId(branchRepoId);
    setViewingSourceTitle(
      branchAnalyses.find((analysis) => String(analysis.repoId) === String(branchRepoId))?.branch ||
        null
    );
    try {
      const response = await api.get(`/repos/${branchRepoId}`);
      setBranchRepo(response.data);
    } catch (err) {
      console.warn("Failed to load branch repo:", err);
      setBranchRepo(null);
    }
  };

  const handleSourcesChange = useCallback((discovered) => {
    setBranchAnalyses((current) => {
      const merged = mergeBranchAnalyses(current, discovered, activeRepo);
      const next = withCanonicalBranchAnalysis(merged, activeRepo);
      return branchAnalysesEqual(current, next) ? current : next;
    });
  }, [activeRepo]);

  const handleAnalysesRefresh = useCallback(async () => {
    if (!activeRepo?._id) return [];
    const data = await listBranchAnalyses(activeRepo._id);
    let merged = withCanonicalBranchAnalysis(data.analyses || [], activeRepo);
    setBranchAnalyses((current) => {
      merged = withCanonicalBranchAnalysis(mergeBranchAnalyses(merged, current, activeRepo), activeRepo);
      return merged;
    });
    return merged;
  }, [activeRepo]);

  const handleOpenSourceAnalysis = async (source) => {
    const repoId = typeof source === "string" ? source : source?.repoId;
    const title =
      (typeof source === "object" && source?.title) ||
      branchAnalyses.find((analysis) => String(analysis.repoId) === String(repoId))?.branch ||
      null;
    if (!repoId) return;

    setViewingSourceTitle(title);
    setSelectedFile(null);
    setActiveTab("files");

    if (String(repoId) === String(activeRepo?._id)) {
      setSelectedBranchRepoId(null);
      setBranchRepo(null);
      return;
    }

    setSelectedBranchRepoId(repoId);
    try {
      const response = await api.get(`/repos/${repoId}`);
      setBranchRepo(response.data);
    } catch (err) {
      console.warn("Failed to load source analysis:", err);
      setBranchRepo(null);
      setError("Could not open that analysis.");
    }
  };

  const analysisRepo = selectedBranchRepoId && branchRepo ? branchRepo : activeRepo;
  const canonicalRepo = activeRepo;
  const liveAnalysisProgress = buildAnalysisProgress({
    processedFiles: Array.isArray(activeRepo?.processedFiles)
      ? activeRepo.processedFiles
      : processingStatus?.processedFilePaths || [],
    processableFiles: processingStatus?.total ?? activeRepo?.processableFiles ?? 0,
    plannedFiles: activeRepo?.prReusePlan,
    relatedFiles: activeRepo?.incrementalAnalysis?.relatedFiles,
    structureFiles: activeRepo?.structure?.files,
  });
  const liveAnalysisStage = deriveAnalysisStage({
    status: processingStatus?.status ?? activeRepo?.status,
    finalizeStatus: processingStatus?.finalizeStatus ?? activeRepo?.finalizeStatus,
    progress: liveAnalysisProgress,
  });

  useEffect(() => {
    if (!initialRepo?._id) return;
    setActiveRepo((prev) => {
      if (!prev || String(prev._id) !== String(initialRepo._id)) {
        return initialRepo;
      }
      if (repoHasExplorerPayload(initialRepo)) {
        return { ...prev, ...initialRepo };
      }
      return { ...initialRepo, ...prev };
    });
  }, [initialRepo]);

  // Single SSE connection per active repo: one effect, refs, cleanup, batched updates
  useEffect(() => {
    const activeRepoId = activeRepo?._id;

    const closeConnection = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      activeRepoIdRef.current = null;
    };

    if (!activeRepoId) {
      closeConnection();
      return closeConnection;
    }

    if (activeRepoIdRef.current === activeRepoId && eventSourceRef.current) {
      return closeConnection;
    }

    closeConnection();

    // SSE auth: EventSource cannot send Authorization headers, so we pass the JWT in the URL.
    // This token is sensitive; server must never log query strings (path-only logging).
    const streamAuthToken = localStorage.getItem("token");
    if (!streamAuthToken) {
      return closeConnection;
    }
    const encodedToken = encodeURIComponent(streamAuthToken);
    const sseUrl = `${api.defaults.baseURL}/repos/${activeRepoId}/stream?token=${encodedToken}`;
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;
    activeRepoIdRef.current = activeRepoId;
    const connectedRepoId = activeRepoId;

    eventSource.onopen = () => {
      if (activeRepoIdRef.current !== connectedRepoId) return;
      // Optional: one log on connect
    };

    const mergeProcessorResults = (incoming, existing) => {
      if (!existing) return incoming || null;
      if (!incoming) return existing;
      const incomingFileCount = incoming.file ? Object.keys(incoming.file).length : 0;
      const incomingRepoCount = incoming.repository ? incoming.repository.length : 0;
      const existingFileCount = existing.file ? Object.keys(existing.file).length : 0;
      const existingRepoCount = existing.repository ? existing.repository.length : 0;
      if (incomingFileCount >= existingFileCount && incomingRepoCount >= existingRepoCount) return incoming;
      if (existingFileCount > incomingFileCount || existingRepoCount > incomingRepoCount) return existing;
      return incoming;
    };

    const flushBatch = () => {
      timerRef.current = null;
      const pending = pendingUpdateRef.current;
      pendingUpdateRef.current = null;
      if (!pending || activeRepoIdRef.current !== pending.repoId) return;
      const data = pending.data;

      setProcessingStatus({
        status: data.status,
        finalizeStatus: data.finalizeStatus,
        processed: data.processed ?? 0,
        total: data.total ?? 0,
        processedFilePaths: data.processedFilePaths,
        fileCapApplied: data.fileCapApplied,
        eligibleFileCount: data.eligibleFileCount,
        selectedFileCount: data.selectedFileCount,
        failedFileCount: data.failedFileCount,
      });
      setProcessingFiles(data.currentlyProcessing ?? []);

      setActiveRepo((current) => {
        const mergedProcessorResults = mergeProcessorResults(data.processorResults, current?.processorResults);
        const hasIncomingStructure = data.structure &&
          typeof data.structure === "object" &&
          !Array.isArray(data.structure) &&
          ((Array.isArray(data.structure.files) && data.structure.files.length > 0) ||
            (Array.isArray(data.structure.folders) && data.structure.folders.length > 0));
        const newStructure = hasIncomingStructure ? data.structure : (current?.structure ?? data.structure ?? null);
        return {
          ...(current || {}),
          ...data,
          status: data.status,
          processedFiles: Array.isArray(data.processedFilePaths)
            ? data.processedFilePaths
            : Array.isArray(data.processed)
              ? data.processed
              : Array.isArray(current?.processedFiles)
                ? current.processedFiles
                : [],
          processableFiles: data.total ?? current?.processableFiles ?? 0,
          processorResults: mergedProcessorResults,
          structure: newStructure,
        };
      });

      if (data.status === "completed" || data.status === "failed") {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setProcessingFiles([]);
        if (data.status === "completed" && fetchFinalDataRef.current) fetchFinalDataRef.current(pending.repoId);
      }
    };

    eventSource.onmessage = (event) => {
      if (activeRepoIdRef.current !== connectedRepoId) return;
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      pendingUpdateRef.current = { data, repoId: connectedRepoId };
      if (!timerRef.current) timerRef.current = setTimeout(flushBatch, BATCH_MS);
    };

    eventSource.onerror = (err) => {
      if (activeRepoIdRef.current !== connectedRepoId) return;
      if (eventSource.readyState === EventSource.CLOSED) {
        eventSource.close();
        setError("Lost connection to server");
      }
    };

    return closeConnection;
  }, [activeRepo?._id]);

  // Completed repos open on Tasks so a founder sees what is moving, not File Explorer.
  useEffect(() => {
    if (!activeRepo?._id) return;
    setActiveTab(activeRepo.status === "completed" ? "work-briefs" : "files");
    // Intentionally keyed to repository identity, not later status refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRepo?._id]);

  useEffect(() => {
    setTasksFilter(initialTasksFilter === "review" ? "review" : "all");
  }, [initialTasksFilter]);

  // If the repo is not completed, the Overview tab is hidden — avoid leaving the user on a blank panel.
  useEffect(() => {
    if (activeRepo?.status !== "completed" && activeTab === "overview") {
      setActiveTab("files");
    }
  }, [activeRepo?.status, activeTab]);

  // Clear prefilled query when switching away from query tab
  useEffect(() => {
    if (activeTab !== 'query' && chatPrefilledQuery) {
      setChatPrefilledQuery(null);
    }
  }, [activeTab, chatPrefilledQuery]);

  // Initialize devMode from localStorage
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const stored = window.localStorage.getItem("devMode");
        setDevMode(stored === "true");
      }
    } catch (e) {
      // fail closed: devMode = false
      console.warn("Failed to read devMode from localStorage", e);
      setDevMode(false);
    }
  }, []);

  // Safety check: reset activeTab if devMode is disabled but we're on traces tab
  useEffect(() => {
    if (!devMode && activeTab === "traces") {
      setActiveTab("query");
    }
  }, [devMode, activeTab]);

  // Fetch available processors when component mounts
  useEffect(() => {
    fetchAvailableProcessors();
  }, []);

  const fetchAvailableProcessors = async () => {
    try {
      setLoadingProcessors(true);
      const response = await api.get("/repos/processors");
      setAvailableProcessors(response.data);
    } catch (err) {
      console.error("Failed to fetch processors:", err);
      setError("Failed to load available processors");
    } finally {
      setLoadingProcessors(false);
    }
  };

  const handleProcessorToggle = (processorId) => {
    setShowEstimate(false);
    setCreditEstimate(null);
    setAnalysisQuoteToken(null);
    setSelectedProcessors((prev) =>
      prev.includes(processorId)
        ? prev.filter((id) => id !== processorId)
        : [...prev, processorId]
    );
  };

  const estimateCredits = async () => {
    const parsed = parseGithubRepoInput(input);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    const { owner, repo, branch, kind, pullNumber } = parsed;

    try {
      setEstimating(true);
      setError("");
      setPrPreflight(null);

      const analysisConfig = {
        maxFileSize: user.settings?.defaultMaxFileSize,
        llmModel: user.settings?.defaultLlmModel,
        processors:
          selectedProcessors.length > 0
            ? { items: selectedProcessors, scope: processorScope }
            : undefined,
      };

      if (kind === "pull" && pullNumber) {
        const response = await preflightGithubPullRequest({
          pullRequestUrl: input.trim(),
          config: analysisConfig,
        });
        const preflight = response.preflight;
        setPrPreflight(preflight);
        setCreditEstimate(preflight.estimate);
        setAnalysisQuoteToken(preflight.quoteToken);
        setShowEstimate(true);
      } else {
      const response = await api.post("/repos/estimate", {
        owner,
        repo,
        ...(branch ? { branch } : {}),
        config: analysisConfig,
      });

      setCreditEstimate(response.data.estimate);
      setAnalysisQuoteToken(response.data.quoteToken);
      setShowEstimate(true);
      }
    } catch (err) {
      console.error("Failed to estimate credits:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Could not load cost preview. Check the repository and try again."
      );
    } finally {
      setEstimating(false);
    }
  };

  const handleSubmit = async (confirmed = false) => {
    if (!confirmed && !creditEstimate) {
      await estimateCredits();
      return;
    }

    if (!confirmed && creditEstimate && !creditEstimate.userHasEnoughCredits) {
      setError(
        `Insufficient credits. You need ${creditEstimate.estimatedCredits.toLocaleString()} credits but only have ${creditEstimate.userCurrentCredits.toLocaleString()}.`
      );
      return;
    }

    const parsedSubmit = parseGithubRepoInput(input);
    if (!parsedSubmit.ok) {
      setError(parsedSubmit.message);
      return;
    }
    const { owner, repo, branch, kind, pullNumber } = parsedSubmit;

    setError("");
    setLoading(true);

    try {
      const analysisConfig = {
        maxFileSize: user.settings?.defaultMaxFileSize,
        llmModel: user.settings?.defaultLlmModel,
        processors:
          selectedProcessors.length > 0
            ? {
                items: selectedProcessors,
                scope: processorScope,
              }
            : undefined,
      };

      const response =
        kind === "pull" && pullNumber
          ? {
              data: await submitGithubPullRequestAnalysis({
                pullRequestUrl: input.trim(),
                config: analysisConfig,
                creditEstimate: creditEstimate?.estimatedCredits,
                quoteToken: analysisQuoteToken,
                confirmed: true,
              }),
            }
          : await api.post("/repos", {
              owner,
              repo,
              ...(branch ? { branch } : {}),
              config: analysisConfig,
              creditEstimate: creditEstimate?.estimatedCredits,
              quoteToken: analysisQuoteToken,
              confirmed: true,
            });

      console.log("POST response:", response.data);

      const newRepo = {
        ...response.data,
        owner: response.data.owner || owner,
        name: response.data.name || repo,
        status: response.data.status || "pending",
        processedFiles: [],
        processableFiles: 0,
      };

      setActiveRepo(newRepo);

      if (onRepoSubmitted) {
        onRepoSubmitted(newRepo);
      }

      setInput("");
      setShowEstimate(false);
      setCreditEstimate(null);
      setAnalysisQuoteToken(null);
    } catch (err) {
      console.error("Submission error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to process repository"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = (path) => {
    const repo = selectedBranchRepoId && branchRepo ? branchRepo : activeRepo;
    if (!repo?.fileSummaries) return;
    const summary = lookupFileSummary(repo.fileSummaries, path);
    setSelectedFile(summary || null);
  };

  const handleOpenChat = (prefilledQuery) => {
    setChatPrefilledQuery(prefilledQuery);
    setActiveTab("query");
  };

  const handleOpenFiles = (filePaths) => {
    setActiveTab("files");
    // Open first file if available
    if (filePaths.length > 0) {
      handleFileClick(filePaths[0]);
    }
  };

  const fetchFinalData = async (repoId) => {
    try {
      const repoResponse = await api.get(`/repos/${repoId}`);
      console.log("fetchFinalData: Received repo data:", {
        hasStructure: !!repoResponse.data.structure,
        structureFiles: repoResponse.data.structure?.files?.length || 0,
        structureFolders: repoResponse.data.structure?.folders?.length || 0,
        status: repoResponse.data.status,
      });
      setActiveRepo((current) => {
        const newRepo = {
          ...current,
          ...repoResponse.data,
          // Explicitly preserve structure if it exists and is valid
          structure: (repoResponse.data.structure && 
                      (repoResponse.data.structure.files?.length > 0 || 
                       repoResponse.data.structure.folders?.length > 0))
                     ? repoResponse.data.structure 
                     : (current?.structure || repoResponse.data.structure),
        };
        console.log("fetchFinalData: Setting activeRepo with structure:", {
          hasStructure: !!newRepo.structure,
          structureFiles: newRepo.structure?.files?.length || 0,
          structureFolders: newRepo.structure?.folders?.length || 0,
        });
        return newRepo;
      });

      const usageResponse = await api.get("/users/profile");
      if (usageResponse.data?.usage) {
        setTokenUsage(usageResponse.data.usage);
      }
    } catch (err) {
      console.error("Failed to fetch final data:", err);
      setError("Failed to fetch final repository data");
    }
  };

  /**
   * Dashboard/profile only returns a shallow repo shape for starters (no structure or fileSummaries).
   * Hydrate from GET /repos/:id so File Explorer and summaries work without relying on SSE alone.
   */
  useEffect(() => {
    const id = activeRepo?._id;
    if (!id || activeRepo.status !== "completed") return;
    const s = activeRepo.structure;
    const hasStructure =
      s &&
      ((Array.isArray(s.files) && s.files.length > 0) ||
        (Array.isArray(s.folders) && s.folders.length > 0));
    const fs = activeRepo.fileSummaries;
    const hasSummaries =
      fs && typeof fs === "object" && !Array.isArray(fs) && Object.keys(fs).length > 0;
    if (hasStructure && hasSummaries) {
      delete completedRepoHydrateAttemptsRef.current[String(id)];
      return;
    }
    const key = String(id);
    const n = (completedRepoHydrateAttemptsRef.current[key] || 0) + 1;
    if (n > 3) return;
    completedRepoHydrateAttemptsRef.current[key] = n;
    fetchFinalData(key);
  }, [activeRepo?._id, activeRepo?.status, activeRepo?.structure, activeRepo?.fileSummaries]);

  useEffect(() => {
    fetchFinalDataRef.current = fetchFinalData;
  });

  return (
    <ErrorBoundary
      showDetails={process.env.NODE_ENV === 'development'}
      onReset={() => {
        setActiveRepo(null);
        setSelectedFile(null);
        setError("");
      }}
    >
      <div className="repo-container">
        <div className="repo-content">
        {!initialRepo && (
          <form className="query-section">
            <h3>Submit Repository</h3>
            <p className="credit-billing-microcopy">
              Repository processing uses credits. Queries are free during
              pre-launch.
            </p>
            <div className="query-input-container">
              <input
                type="text"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setShowEstimate(false);
                  setCreditEstimate(null);
                  setAnalysisQuoteToken(null);
                }}
                placeholder="GitHub repo or pull request URL (e.g. facebook/react or github.com/org/repo/pull/123)"
                className="query-input"
                disabled={loading}
              />
              <button
                type="button"
                className="secondary-button"
                onClick={estimateCredits}
                disabled={loading || estimating}
              >
                {estimating
                  ? "Getting cost preview…"
                  : "Preview cost (no credits used)"}
              </button>
            </div>

            {/* Simplified Processor Selection */}
            {availableProcessors.length > 0 && (
              <div className="processors-section">
                <h4>Additional Processing (Optional)</h4>
                <div className="processor-checkboxes">
                  {availableProcessors.map((processor) => (
                    <label key={processor.id} className="processor-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedProcessors.includes(processor.id)}
                        onChange={() => handleProcessorToggle(processor.id)}
                        disabled={loading}
                      />
                      <div className="processor-info">
                        <span className="processor-name">{processor.name}</span>
                        <span className="processor-description">
                          {processor.description}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
                
                {selectedProcessors.length > 0 && (
                  <div className="processor-scope-selection">
                    <label>Processing scope:</label>
                    <select 
                      value={processorScope} 
                      onChange={(e) => {
                        setProcessorScope(e.target.value);
                        setShowEstimate(false);
                        setCreditEstimate(null);
                        setAnalysisQuoteToken(null);
                      }}
                      disabled={loading}
                    >
                      <option value="file">File-level (process individual files)</option>
                      <option value="repository">Repository-level (process after all files)</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {showEstimate && creditEstimate && (
              <RepositoryAnalysisConfirmation
                estimate={creditEstimate}
                prPreflight={prPreflight}
                loading={loading}
                onConfirm={() => handleSubmit(true)}
                onPurchase={() => setShowCreditPurchase(true)}
                onCancel={() => {
                  setShowEstimate(false);
                  setCreditEstimate(null);
                  setAnalysisQuoteToken(null);
                  setPrPreflight(null);
                }}
              />
            )}

            {error && <p className="error-message">{error}</p>}
          </form>
        )}

        {activeRepo && (
          <>
            <div className="grid-layout">
              <div className="query-section">
                <h2>
                  {activeRepo.owner}/{activeRepo.name}
                  {viewingSourceTitle ||
                  branchDisplayNameFromSource(analysisRepo?.analysisSource)
                    ? ` · ${
                        viewingSourceTitle ||
                        branchDisplayNameFromSource(analysisRepo?.analysisSource)
                      }`
                    : ""}
                </h2>
                {(activeRepo.analysisSource?.commitSha || activeRepo.prIntake) && activeTab !== "work-briefs" && (
                  <p className="repo-sha-pin" data-testid="repo-sha-pin">
                    {activeRepo.prIntake
                      ? `PR #${activeRepo.prIntake.pullNumber} · ${activeRepo.prIntake.mode} · head ${String(activeRepo.analysisSource?.commitSha || activeRepo.prIntake.head?.sha || "").slice(0, 12)}`
                      : `Pinned ${String(activeRepo.analysisSource.commitSha).slice(0, 12)}`}
                  </p>
                )}
                {processingStatus && activeTab !== "work-briefs" && (
                  <>
                    <div className="processing-status">
                      {processingStatus.status === "completed"
                        ? "Analysis complete"
                        : formatAnalysisStageLabel(liveAnalysisStage)}
                      : {processingStatus.processed} /{" "}
                      {processingStatus.total} files
                    </div>
                    {(processingStatus.fileCapApplied || activeRepo?.analysisCoverage?.fileCapApplied) && (
                      <div className="processing-coverage-note" title={`${processingStatus.eligibleFileCount ?? activeRepo?.analysisCoverage?.eligibleFileCount ?? 0} eligible; analysis limited to ${processingStatus.selectedFileCount ?? processingStatus.total ?? activeRepo?.analysisCoverage?.selectedFileCount ?? 0} files`}>
                        Analysis capped at {processingStatus.selectedFileCount ?? processingStatus.total ?? activeRepo?.analysisCoverage?.selectedFileCount ?? processingStatus.total ?? 0} files
                      </div>
                    )}
                    {((processingStatus.failedFileCount ?? 0) > 0 || (activeRepo?.analysisCoverage?.failedFileCount ?? 0) > 0) && (
                      <div className="processing-failed-note">
                        {(processingStatus.failedFileCount ?? activeRepo?.analysisCoverage?.failedFileCount ?? 0)} file{(processingStatus.failedFileCount ?? activeRepo?.analysisCoverage?.failedFileCount ?? 0) !== 1 ? "s" : ""} failed
                      </div>
                    )}
                    <ProgressBar
                      processed={processingStatus.processed}
                      total={processingStatus.total}
                      stage={liveAnalysisStage}
                    />
                    {processingStatus.status !== "completed" && liveAnalysisProgress.hasFileList && (
                      <div
                        className="processing-file-progress"
                        data-testid="repo-analysis-progress-files"
                      >
                        <p>{formatAnalysisProgressCopy(liveAnalysisProgress, { stage: liveAnalysisStage })}</p>
                        {liveAnalysisProgress.remainingPreview.length > 0 && (
                          <>
                            <h3>Still to analyze</h3>
                            <ul>
                              {liveAnalysisProgress.remainingPreview.map((path) => (
                                <li key={path}>{path}</li>
                              ))}
                            </ul>
                            {liveAnalysisProgress.remainingOverflow > 0 && (
                              <p className="processing-file-overflow">
                                +{liveAnalysisProgress.remainingOverflow} more
                              </p>
                            )}
                          </>
                        )}
                        {liveAnalysisProgress.donePreview.length > 0 && (
                          <>
                            <h3>Files analyzed</h3>
                            <ul>
                              {liveAnalysisProgress.donePreview.map((path) => (
                                <li key={path}>{path}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
                {!processingStatus && activeRepo?.status === "completed" && activeRepo?.analysisCoverage && (activeRepo.analysisCoverage.fileCapApplied || (activeRepo.analysisCoverage.failedFileCount ?? 0) > 0) && (
                  <div className="processing-coverage-badges">
                    {activeRepo.analysisCoverage.fileCapApplied && (
                      <div className="processing-coverage-note" title={`${activeRepo.analysisCoverage.eligibleFileCount ?? 0} eligible; ${activeRepo.analysisCoverage.selectedFileCount ?? 0} analyzed`}>
                        Analysis capped at {activeRepo.analysisCoverage.selectedFileCount ?? 0} files
                      </div>
                    )}
                    {(activeRepo.analysisCoverage.failedFileCount ?? 0) > 0 && (
                      <div className="processing-failed-note">
                        {activeRepo.analysisCoverage.failedFileCount} file{activeRepo.analysisCoverage.failedFileCount !== 1 ? "s" : ""} failed
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {activeRepo.status === "completed" &&
              activeRepo.analysisCoverage?.coverageIntegrity !== "OK" && (
                <div
                  className="repo-degraded-analysis-banner"
                  role="status"
                >
                  ⚠️ This repository was only partially analyzed. Answers and
                  insights may be incomplete.
                </div>
              )}

            {(viewingSourceTitle || selectedBranchRepoId) && activeTab !== "source-compare" && (
              <div className="viewing-source-banner" data-testid="viewing-source-banner" role="status">
                <span>
                  Viewing {viewingSourceTitle || "this source"} in File Explorer.
                  Source Compare still has every source if you want to compare.
                </span>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setActiveTab("source-compare")}
                >
                  Back to Source Compare
                </button>
              </div>
            )}

            {/* Branch Analysis Selector */}
            {activeRepo && activeTab !== "work-briefs" && activeTab !== "advanced" && (
              <div className="branch-selector" data-testid="branch-analysis-selector">
                <GitBranch size={16} />
                <select
                  aria-label="Viewed analysis"
                  value={selectedBranchRepoId || activeRepo._id}
                  onChange={(e) => handleBranchSwitch(e.target.value === activeRepo._id ? null : e.target.value)}
                >
                  {(branchAnalyses.length
                    ? branchAnalyses
                    : withCanonicalBranchAnalysis([], activeRepo)
                  ).map((b) => (
                    <option key={b.repoId} value={b.repoId}>
                      {formatBranchSelectorLabel(b, {
                        isStartingAnalysis: String(b.repoId) === String(activeRepo._id),
                      })}
                    </option>
                  ))}
                </select>
                {selectedBranchRepoId && (
                  <span className="branch-selector-note">
                    File Explorer and Query show this branch. Source Compare still lists every source.
                  </span>
                )}
              </div>
            )}

            {/* Repository Tabs */}
            <div className="repository-tabs">
              <div className="tabs-header">
                <button
                  type="button"
                  className={`tab ${activeTab === "work-briefs" && tasksFilter !== "review" ? "active" : ""}`}
                  data-testid="repo-tab-tasks"
                  onClick={() => {
                    setTasksFilter("all");
                    setWorkBriefPlanId(null);
                    setActiveTab("work-briefs");
                    if (typeof onTaskRoute === "function" && activeRepo?._id) {
                      onTaskRoute(taskPath({ repositoryId: activeRepo._id }));
                    }
                  }}
                >
                  <Wand2 size={16} />
                  Tasks
                </button>
                <button
                  type="button"
                  className={`tab ${activeTab === "roadmap" ? "active" : ""}`}
                  data-testid="repo-tab-roadmap"
                  onClick={() => setActiveTab("roadmap")}
                >
                  <Map size={16} />
                  Roadmap
                </button>
                <button
                  type="button"
                  className={`tab ${activeTab === "files" ? "active" : ""}`}
                  data-testid="repo-tab-repository"
                  onClick={() => setActiveTab("files")}
                >
                  <FileText size={16} />
                  Repository
                </button>
                <button
                  type="button"
                  className={`tab ${activeTab === "work-briefs" && tasksFilter === "review" ? "active" : ""}`}
                  data-testid="repo-tab-reviews"
                  onClick={() => {
                    setTasksFilter("review");
                    setActiveTab("work-briefs");
                    if (typeof onTaskRoute === "function" && activeRepo?._id) {
                      onTaskRoute(taskPath({ repositoryId: activeRepo._id, view: "reviews" }));
                    }
                  }}
                >
                  <GitCompare size={16} />
                  Reviews
                </button>
                <button
                  type="button"
                  className={`tab ${
                    ["advanced", "overview", "notes", "source-compare", "traces", "query"].includes(
                      activeTab
                    )
                      ? "active"
                      : ""
                  }`}
                  data-testid="repo-tab-advanced"
                  onClick={() => setActiveTab("advanced")}
                >
                  <Settings size={16} />
                  Advanced
                </button>
              </div>

              <div className="tab-content">
                {activeTab === "advanced" ? (
                  <section className="advanced-tools" data-testid="advanced-tools">
                    <header className="advanced-tools-header">
                      <h2>Advanced</h2>
                      <p>
                        Extra repository tools. Everyday work lives under Tasks, Roadmap, and Reviews.
                      </p>
                    </header>
                    <div className="advanced-tools-grid">
                      {activeRepo.status === "completed" ? (
                        <button
                          type="button"
                          className="advanced-tool"
                          onClick={() => setActiveTab("overview")}
                        >
                          <BookOpen size={18} />
                          <strong>Codebase Guide</strong>
                          <span>How this repository is structured and what to watch for.</span>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="advanced-tool"
                        onClick={() => setActiveTab("notes")}
                      >
                        <StickyNote size={18} />
                        <strong>Notes</strong>
                        <span>Private notes about this repository.</span>
                      </button>
                      <button
                        type="button"
                        className="advanced-tool"
                        onClick={() => setActiveTab("query")}
                      >
                        <MessageSquare size={18} />
                        <strong>Ask the repository</strong>
                        <span>Ask questions about the indexed code.</span>
                      </button>
                      {activeRepo.status === "completed" ? (
                        <button
                          type="button"
                          className="advanced-tool"
                          onClick={() => setActiveTab("source-compare")}
                        >
                          <GitCompare size={18} />
                          <strong>Source Compare</strong>
                          <span>Compare another branch or pull request with this repository.</span>
                        </button>
                      ) : null}
                      {devMode ? (
                        <button
                          type="button"
                          className="advanced-tool"
                          onClick={() => setActiveTab("traces")}
                        >
                          <Cpu size={18} />
                          <strong>Traces</strong>
                          <span>Internal processing traces for this analysis.</span>
                        </button>
                      ) : null}
                    </div>
                  </section>
                ) : null}

                {activeTab === "overview" && analysisRepo.status === 'completed' && (
                  <SystemOverview
                    repo={analysisRepo}
                    onOpenChat={handleOpenChat}
                    onOpenFiles={handleOpenFiles}
                    onRepoUpdate={(updatedRepo) => {
                      setActiveRepo(updatedRepo);
                    }}
                    metadataLoading={metadataLoading}
                    isAdmin={!!user?.isAdmin}
                  />
                )}

                {activeTab === "files" && (
                  <div className="grid-layout">
                    <div className="query-section">
                      <h2>Repository Structure</h2>
                      {analysisRepo.structure &&
                       (analysisRepo.structure.files?.length > 0 || analysisRepo.structure.folders?.length > 0) ? (
                        <FileTree
                          structure={analysisRepo.structure}
                          fileSummaries={analysisRepo.fileSummaries}
                          onFileClick={handleFileClick}
                          processingFiles={processingFiles}
                          notes={notes}
                        />
                      ) : (
                        <div style={{ padding: '20px', color: '#666' }}>
                          {analysisRepo.status === 'pending' || analysisRepo.status === 'processing' ? (
                            <div>Re-analyzing repository…</div>
                          ) : analysisRepo.status === 'completed' && !structureHydrationTimedOut ? (
                            <div>Loading repository structure…</div>
                          ) : analysisRepo.status === 'completed' ? (
                            <div>
                              <p>No structure data available.</p>
                              <p style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
                                Try refreshing the page. If this persists, the repo may not have a stored file
                                tree.
                              </p>
                            </div>
                          ) : (
                            <div>Re-analyzing repository…</div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      {selectedFile && (
                        <div className="query-section">
                          <FileSummary summary={selectedFile} />

                          <FileContent
                            owner={analysisRepo.owner}
                            repo={analysisRepo.name}
                            path={selectedFile.path}
                            visibility={analysisRepo.visibility}
                            isPrivate={analysisRepo.visibility === "private"}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "query" && (
                  <div className="query-section query-section-chat">
                    <RepoChatWindow
                      repoId={analysisRepo._id}
                      repoStatus={analysisRepo.status}
                      prefilledQuery={chatPrefilledQuery}
                      onNavigateToFeaturePlans={() => setActiveTab("work-briefs")}
                    />
                  </div>
                )}

                {activeTab === "notes" && (
                  <NotesPanel 
                    repoId={activeRepo._id} 
                    onNotesChange={async () => {
                      // Refresh notes when they change in NotesPanel
                      try {
                        const response = await api.getNotes(activeRepo._id);
                        setNotes(response.data.notes || []);
                      } catch (err) {
                        console.warn('Failed to refresh notes:', err);
                      }
                    }}
                  />
                )}

                {activeTab === "roadmap" && (
                  <RoadmapPanel
                    repo={activeRepo}
                    onOpenWorkBrief={(planId) => {
                      setWorkBriefPlanId(planId || null);
                      setActiveTab("work-briefs");
                    }}
                  />
                )}

                {activeTab === "work-briefs" && (
                  <FeaturePlansPanel
                    repo={activeRepo}
                    initialSelectedPlanId={workBriefPlanId || initialTaskId}
                    initialTaskSection={initialTaskSection}
                    initialTaskDiscuss={initialTaskDiscuss}
                    initialTaskFocus={initialTaskFocus}
                    initialTaskItem={initialTaskItem}
                    tasksFilter={tasksFilter}
                    onOpenRoadmap={() => setActiveTab("roadmap")}
                    onTaskRoute={onTaskRoute}
                  />
                )}

                {canonicalRepo?.status === "completed" && (
                  <div
                    className="query-section"
                    style={activeTab === "source-compare" ? undefined : { display: "none" }}
                    aria-hidden={activeTab !== "source-compare"}
                  >
                    <SourceComparePanel
                      repo={canonicalRepo}
                      branchAnalyses={branchAnalyses}
                      canonicalRepoId={canonicalRepo._id}
                      canAddSource={canMutateRepos(user)}
                      onOpenNewRepository={onOpenNewRepository}
                      onOpenSource={handleOpenSourceAnalysis}
                      onSourcesChange={handleSourcesChange}
                      onAnalysesRefresh={handleAnalysesRefresh}
                    />
                  </div>
                )}

                {devMode && activeTab === "traces" && (
                  <RepoTracesPanel repoId={analysisRepo._id} />
                )}
              </div>
            </div>
            {tokenUsage && tokenUsage.usageByModel && activeTab !== "source-compare" && activeTab !== "work-briefs" && activeTab !== "advanced" && (
              <div className="token-usage">
                <h3>Token Usage</h3>
                <div className="usage-details">
                  {Object.entries(tokenUsage.usageByModel || {}).map(
                    ([model, usage]) => (
                      <div key={model} className="model-usage">
                        <h4>{model}</h4>
                        <p>
                          Input tokens: {usage?.input?.toLocaleString() || 0}
                        </p>
                        <p>
                          Output tokens: {usage?.output?.toLocaleString() || 0}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

        {showCreditPurchase && (
          <CreditPurchaseModal onClose={() => setShowCreditPurchase(false)} />
        )}

      <style jsx>{`
        .processors-section {
          margin-top: 20px;
          padding: 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
        }

        .processors-section h4 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
        }

        .processor-checkboxes {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .processor-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .processor-checkbox:hover {
          background-color: #f8fafc;
        }

        .processor-checkbox input[type="checkbox"] {
          margin: 0;
          transform: scale(1.2);
        }

        .processor-info {
          flex: 1;
        }

        .processor-name {
          display: block;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .processor-description {
          display: block;
          font-size: 14px;
          color: #64748b;
        }

        .credit-estimate {
          margin-top: 20px;
          padding: 20px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background: #f8fafc;
        }

        .estimate-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }

        .estimate-header h4 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .estimate-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .estimate-main {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .estimate-credits {
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
        }

        .estimate-files {
          font-size: 14px;
          color: #64748b;
        }

        .estimate-status {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
        }

        .estimate-status.sufficient {
          background: #dcfce7;
          color: #166534;
        }

        .estimate-status.insufficient {
          background: #fef2f2;
          color: #dc2626;
        }

        .estimate-actions {
          display: flex;
          gap: 12px;
        }

        .secondary-button {
          padding: 8px 16px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .secondary-button:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .btn-primary {
          padding: 10px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-primary:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .btn-secondary {
          padding: 10px 20px;
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }
      `}</style>
      </div>
    </ErrorBoundary>
  );
};

export default RepoAnalyzer;
